import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { en, type TranslationKey } from "./en";
import { ptBR } from "./pt-BR";

type Language = "en" | "pt-BR";

const translations = {
  en,
  "pt-BR": ptBR,
};

interface I18nContextValue {
  language: Language;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
  setLanguage: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
}

export function I18nProvider({ children, initialLanguage = "en" }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string>): string => {
      let text = translations[language][key] || translations.en[key] || key;

      if (params) {
        Object.entries(params).forEach(([paramKey, value]) => {
          text = text.replace(`{${paramKey}}`, value);
        });
      }

      return text;
    },
    [language]
  );

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  return (
    <I18nContext.Provider value={{ language, t, setLanguage }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

export type { TranslationKey, Language };
